import React, { memo } from "react";
import PropTypes from "prop-types";
import * as styles from "./text.module.scss";

/**
 * Get the default HTML element for a given text variant.
 * @param {string} element - The text variant.
 * @returns {string} The corresponding HTML element.
 */

const getDefaultElement = (element) => {
  switch (element) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return element;
    case "eyebrow":
    case "menu":
      return "span";
    case "p1":
    case "p2":
    case "p3":
    case "p4":
    case "p5":
    default:
      return "p";
  }
};

/**
 * Text component for rendering various text elements with different styles.
 * @param {object} props - The props for the component.
 * @param {string} props.variant - The text variant (e.g., "h1", "p1-bold-italic").
 * @param {string} [props.as] - The HTML element to render (overrides default).
 * @param {string} [props.className] - Additional class names to apply.
 * @param {React.ReactNode} props.children - The content to render inside the text element.
 * @param {string} [props.role] - ARIA role for accessibility.
 * @param {string} [props.ariaLabel] - ARIA label for accessibility.
 * @param {number} [props.ariaLevel] - ARIA level for heading roles (1-6).
 * @returns {JSX.Element} The rendered text component.
 */

const Text = memo(({ variant = "p1", as, className = "", children, role, ariaLabel, ariaLevel, ...props }) => {
  const variantParts = variant.split("-");
  const element = variantParts[0];
  const stylesArray = variantParts.slice(1);
  const combinedClassName = `${styles[element]} ${stylesArray
    .map((style) => styles[style] || "")
    .join(" ")} ${className}`;

  const Component = as || getDefaultElement(element);

  return (
    <Component
      className={combinedClassName}
      role={role}
      aria-label={ariaLabel}
      aria-level={role === "heading" ? ariaLevel || 2 : undefined}
      data-transition={props["data-transition"]}
    >
      {children}
    </Component>
  );
});

Text.propTypes = {
  variant: PropTypes.string,
  as: PropTypes.oneOfType([
    PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "div"]),
    PropTypes.string,
  ]),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  role: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLevel: PropTypes.number,
};

Text.displayName = "Text";

export { Text };
